// Action Node Component

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Card,
  CardContent
} from '@mui/material';
import {
  PlayArrow as ActionIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';

interface ActionNodeData {
  label: string;
  description?: string;
  connectorId?: string;
  actionId?: string;
  connector?: {
    name: string;
    icon: string;
    color: string;
  };
  status?: 'idle' | 'running' | 'success' | 'error';
  executionTime?: number;
  error?: string;
}

const ActionNode: React.FC<NodeProps<ActionNodeData>> = ({ 
  data, 
  selected, 
  id 
}) => {
  const getStatusIcon = () => {
    switch (data.status) {
      case 'running':
        return <ActionIcon sx={{ color: '#2196F3', animation: 'spin 1s linear infinite' }} />;
      case 'success':
        return <SuccessIcon sx={{ color: '#4CAF50' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#F44336' }} />;
      default:
        return <ActionIcon sx={{ color: '#757575' }} />;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'running':
        return '#E3F2FD';
      case 'success':
        return '#E8F5E9';
      case 'error':
        return '#FFEBEE';
      default:
        return '#F5F5F5';
    }
  };

  const getBorderColor = () => {
    if (selected) return '#1976D2';
    switch (data.status) {
      case 'running':
        return '#2196F3';
      case 'success':
        return '#4CAF50';
      case 'error':
        return '#F44336';
      default:
        return '#E0E0E0';
    }
  };

  return (
    <Box sx={{ minWidth: 200 }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />
      
      <Card
        sx={{
          backgroundColor: getStatusColor(),
          border: `2px solid ${getBorderColor()}`,
          borderRadius: 2,
          minHeight: 80,
          transition: 'all 0.2s ease',
          boxShadow: selected ? 4 : 1,
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)'
          }
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Header with Icon and Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ mr: 1 }}>
              {data.connector ? (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: data.connector.color,
                    fontSize: '0.8rem'
                  }}
                >
                  {data.connector.name.charAt(0)}
                </Avatar>
              ) : (
                getStatusIcon()
              )}
            </Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                flexGrow: 1,
                fontSize: '0.9rem'
              }}
            >
              {data.label}
            </Typography>
            <Box sx={{ ml: 1 }}>
              {getStatusIcon()}
            </Box>
          </Box>

          {/* Description */}
          {data.description && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mb: 1,
                fontSize: '0.75rem',
                lineHeight: 1.2
              }}
            >
              {data.description}
            </Typography>
          )}

          {/* Connector and Action Info */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {data.connector && (
              <Chip
                label={data.connector.name}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  backgroundColor: `${data.connector.color}20`,
                  color: data.connector.color,
                  border: `1px solid ${data.connector.color}40`
                }}
              />
            )}
            {data.actionId && (
              <Chip
                label={data.actionId}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
          </Box>

          {/* Execution Info */}
          {(data.executionTime || data.error) && (
            <Box sx={{ mt: 1 }}>
              {data.executionTime && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {data.executionTime}ms
                </Typography>
              )}
              {data.error && (
                <Typography
                  variant="caption"
                  sx={{ color: 'error.main', display: 'block' }}
                >
                  {data.error}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />

      {/* Execution Status Indicator */}
      {data.status === 'running' && (
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#2196F3',
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 1
              },
              '50%': {
                transform: 'scale(1.3)',
                opacity: 0.7
              },
              '100%': {
                transform: 'scale(1)',
                opacity: 1
              }
            }
          }}
        />
      )}

      {/* Add spinning animation keyframes */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default memo(ActionNode);